import { toAssetCdnUrl, isCdnConfigured, isS3ImageCdnConfigured } from './assetCdnUrl.js';

const API_BASE = String(import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, '');
const DISABLE_IMAGE_DELIVERY =
  String(import.meta.env.VITE_DISABLE_IMAGE_DELIVERY || '').toLowerCase() === 'true' ||
  isS3ImageCdnConfigured();

function isDirectS3Url(url) {
  return /amazonaws\.com/i.test(String(url || ''));
}

/** Primary display URL. Never fall back to raw S3 when CDN is configured (billing). */
export function resolveImageDisplayUrls(url, options = {}) {
  const raw = String(url || '').trim();
  if (!raw || raw.startsWith('data:') || raw.startsWith('blob:')) {
    return { src: raw, fallbackSrc: null };
  }

  const src = optimizeImageUrl(raw, options);
  // Falling back to amazonaws.com re-opens S3 egress — only when CDN is not configured.
  const fallbackSrc =
    !isS3ImageCdnConfigured() && src !== raw && isDirectS3Url(raw) ? raw : null;
  return { src, fallbackSrc };
}

export function imageLoadErrorFallback(currentSrc, fallbackSrc, placeholder) {
  const cur = String(currentSrc || '').trim();
  const fb = String(fallbackSrc || '').trim();
  if (fb && cur !== fb) return fb;
  return placeholder;
}

/** Display widths — keep in sync with layout (card ~720px, hero ~1600px). */
export const IMAGE_DISPLAY_WIDTH = {
  hero: 1600,
  card: 720,
  thumb: 400,
  gallery: 1200
};

function isOptimizableRemoteUrl(url) {
  return /amazonaws\.com/i.test(url) || /cloudfront\.net/i.test(url) || /images\.unsplash\.com/i.test(url);
}

/**
 * Return a CDN URL for display. With VITE_ASSET_CDN_URL set, images load from CloudFront
 * (cached at edge) instead of the API resize proxy — much lower AWS cost.
 */
export function optimizeImageUrl(url, { width = IMAGE_DISPLAY_WIDTH.card, quality = 82 } = {}) {
  const raw = String(url || '').trim();
  if (!raw || raw.startsWith('data:') || raw.startsWith('blob:')) return raw;
  if (/\/images\/delivery/i.test(raw)) return raw;

  if (/images\.unsplash\.com/i.test(raw)) {
    if (/[?&]w=\d/i.test(raw)) return raw;
    const sep = raw.includes('?') ? '&' : '?';
    return `${raw}${sep}w=${width}&q=80&auto=format&fit=crop`;
  }

  if (/amazonaws\.com/i.test(raw) || /cloudfront\.net/i.test(raw)) {
    const cdnUrl = toAssetCdnUrl(raw);
    if (DISABLE_IMAGE_DELIVERY || cdnUrl !== raw) {
      return cdnUrl;
    }
    if (API_BASE) {
      const params = new URLSearchParams({
        src: raw,
        w: String(width),
        q: String(quality)
      });
      return `${API_BASE}/images/delivery?${params.toString()}`;
    }
    return cdnUrl;
  }

  // Relative S3 object keys → CDN when configured
  if (isS3ImageCdnConfigured() && !raw.startsWith('/') && !raw.includes('://')) {
    return toAssetCdnUrl(raw);
  }

  return raw;
}

export function optimizeImageUrlIfRemote(url, options) {
  const raw = String(url || '').trim();
  if (!raw || !isOptimizableRemoteUrl(raw)) return raw;
  return optimizeImageUrl(raw, options);
}

export { toAssetCdnUrl, isCdnConfigured, isS3ImageCdnConfigured } from './assetCdnUrl.js';
